import type { WelcomeNotificationDto } from '@/lib/dtos';

export const WELCOME_DELAY_MINUTES = [
  0, 5, 10, 30, 45, 60, 120, 240, 360, 720, 1440, 2880, 4320, 7200, 10080,
] as const;

export type WelcomeDelay = (typeof WELCOME_DELAY_MINUTES)[number];

/** Na hora, 10 min, 1 h, 1 dia */
export const WELCOME_DELAY_CHIPS: readonly WelcomeDelay[] = [0, 10, 60, 1440];

export function isWelcomeDelay(value: number): value is WelcomeDelay {
  return WELCOME_DELAY_MINUTES.some((delay) => delay === value);
}

export type DelayParts =
  | { kind: 'immediately' }
  | { kind: 'minutes' | 'hours' | 'days'; amount: number };

export function getDelayParts(minutes: WelcomeDelay): DelayParts {
  if (minutes === 0) return { kind: 'immediately' };
  if (minutes < 60) return { kind: 'minutes', amount: minutes };
  if (minutes < 1440) return { kind: 'hours', amount: minutes / 60 };
  return { kind: 'days', amount: minutes / 1440 };
}

export interface WelcomeNotificationForm {
  isActive: boolean;
  title: string;
  body: string;
  link: string;
  delayMinutes: WelcomeDelay;
}

export const DEFAULT_WELCOME_FORM: WelcomeNotificationForm = {
  isActive: false,
  title: '',
  body: '',
  link: '',
  delayMinutes: 0,
};

export function welcomeDtoToForm(
  dto: WelcomeNotificationDto | null
): WelcomeNotificationForm {
  if (!dto) return DEFAULT_WELCOME_FORM;
  return {
    isActive: dto.isActive,
    title: dto.title,
    body: dto.body,
    link: dto.link,
    delayMinutes: isWelcomeDelay(dto.delayMinutes) ? dto.delayMinutes : 0,
  };
}

export function welcomeFormToDto(
  form: WelcomeNotificationForm
): WelcomeNotificationDto {
  return {
    isActive: form.isActive,
    title: form.title,
    body: form.body,
    link: form.link,
    delayMinutes: form.delayMinutes,
  };
}

export type WelcomeFieldError =
  | 'titleRequired'
  | 'bodyRequired'
  | 'linkRequired'
  | 'domainNotConfigured'
  | 'linkPrefix';

export function validateWelcomeForm(
  form: WelcomeNotificationForm,
  storeWebsiteDomain: string | null
): Partial<Record<'title' | 'body' | 'link', WelcomeFieldError>> {
  const errors: Partial<Record<'title' | 'body' | 'link', WelcomeFieldError>> =
    {};
  if (!form.title.trim()) errors.title = 'titleRequired';
  if (!form.body.trim()) errors.body = 'bodyRequired';
  if (!form.link.trim()) {
    errors.link = 'linkRequired';
  } else if (!storeWebsiteDomain) {
    errors.link = 'domainNotConfigured';
  } else if (!form.link.startsWith(`https://${storeWebsiteDomain}`)) {
    errors.link = 'linkPrefix';
  }
  return errors;
}

export function isWelcomeFormDirty(
  baseline: WelcomeNotificationForm,
  form: WelcomeNotificationForm
): boolean {
  return (
    baseline.isActive !== form.isActive ||
    baseline.title !== form.title ||
    baseline.body !== form.body ||
    baseline.link !== form.link ||
    baseline.delayMinutes !== form.delayMinutes
  );
}
